// Copy / share / download helpers for images, shared by any screen that
// shows pictures staff may want to send on (e.g. the Answer Desk).
//
// Browsers only accept PNG on the clipboard, so every copy goes through a
// canvas. The image bytes come from a `loader` the caller supplies (usually
// an authenticated API request) because our storage domain sends no CORS
// headers, so a plain <img> can't be read back.

export type ImageLoader = () => Promise<Blob>;

/** How "Copy" will behave on this device. */
export type ImageCopyMode = "copy" | "share" | "download";

export function canCopyImages(): boolean {
    return (
        typeof window !== "undefined" &&
        window.isSecureContext &&
        typeof ClipboardItem !== "undefined" &&
        !!navigator.clipboard?.write
    );
}

function canShareFiles(): boolean {
    if (typeof navigator === "undefined" || !navigator.canShare) return false;
    try {
        return navigator.canShare({ files: [new File([""], "probe.jpg", { type: "image/jpeg" })] });
    } catch {
        return false;
    }
}

const isTouchDevice = (): boolean =>
    typeof window !== "undefined" && window.matchMedia?.("(pointer: coarse)").matches === true;

/**
 * Desktop browsers copy to the clipboard. Phones get the share sheet
 * (straight into Messenger/WhatsApp) when they support sharing files —
 * mobile clipboards often can't hold images — and download otherwise.
 */
export function imageCopyMode(): ImageCopyMode {
    if (isTouchDevice() && canShareFiles()) return "share";
    if (canCopyImages()) return "copy";
    if (canShareFiles()) return "share";
    return "download";
}

export async function toPngBlob(blob: Blob): Promise<Blob> {
    if (blob.type === "image/png") return blob;
    const url = URL.createObjectURL(blob);
    try {
        const img = await new Promise<HTMLImageElement>((resolve, reject) => {
            const el = new Image();
            el.onload = () => resolve(el);
            el.onerror = () => reject(new Error("Couldn't read the image"));
            el.src = url;
        });
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        if (!ctx) throw new Error("Canvas isn't available");
        ctx.drawImage(img, 0, 0);
        return await new Promise<Blob>((resolve, reject) =>
            canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("Couldn't convert the image"))), "image/png"),
        );
    } finally {
        URL.revokeObjectURL(url);
    }
}

/**
 * Must be called straight from a click: the ClipboardItem is created
 * synchronously with a pending blob, which is what Safari requires.
 */
export async function copyImage(loader: ImageLoader): Promise<void> {
    await navigator.clipboard.write([
        new ClipboardItem({ "image/png": loader().then(toPngBlob) }),
    ]);
}

export async function shareImage(blob: Blob, filename: string, title?: string): Promise<void> {
    const file = new File([blob], filename, { type: blob.type || "image/jpeg" });
    await navigator.share({ files: [file], title });
}

export function downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    // Give the browser a moment to start the download before freeing it.
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export type CopyOutcome = "copied" | "shared" | "downloaded" | "cancelled";

/** "Copy" with graceful fallbacks: clipboard → share sheet → download. */
export async function copyImageWithFallback(
    loader: ImageLoader,
    filename: string,
    title?: string,
): Promise<CopyOutcome> {
    const mode = imageCopyMode();
    if (mode === "copy") {
        try {
            await copyImage(loader);
            return "copied";
        } catch {
            /* clipboard refused — fall through */
        }
    }
    const blob = await loader();
    if (mode !== "download" && canShareFiles()) {
        try {
            await shareImage(blob, filename, title);
            return "shared";
        } catch (err) {
            if ((err as DOMException)?.name === "AbortError") return "cancelled";
        }
    }
    downloadBlob(blob, filename);
    return "downloaded";
}

/** "size-chart" style file stem; falls back when the text has no Latin letters. */
export function fileStem(text: string, fallback = "image"): string {
    const stem = text
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "")
        .slice(0, 60);
    return stem || fallback;
}
